/**
 * Auth constants and utilities for ERP login integration.
 */

import {
  AUTH_COOKIE_MAX_AGE,
  AUTH_COOKIE_NAME,
  AUTH_KEY_STORAGE,
  REFRESH_MAX_AGE,
  REFRESH_NAME,
} from '../utilities/constant.ts'
import { clearStoredProfile } from '@/lib/profile'
import type { RequestCookies } from 'next/dist/compiled/@edge-runtime/cookies'
import type { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies'

export interface LoginSuccessResponse {
  message: string
  home_page: string
  full_name: string
  first_name: string | null
  last_name: string | null
  status_code: number
  email: string | null
  mobile_no: string | null
  location: string | null
  region: string | null
  tax_id: string | null
  company: string | null
  auth_key: string
  api_key: string
  api_secret: string
  session: string
  sid: string
  refresh_token: string
  refresh_token_expiry: string
  updatedCookie: string
}

export interface LoginErrorResponse {
  exc?: string
  _server_messages?: string
  message?: string
  exception?: string
}

const SENSITIVE_CACHE_PREFIXES = ['gallium-auth-api-', 'gallium-api-']
const SENSITIVE_SNAPSHOT_KEYS = [
  'gallium_dashboard_snapshot_v1',
  'gallium_dealer_cart_snapshot_v1',
  'gallium_enquiry_cart_snapshot_v1',
]

async function clearSensitiveClientCaches(): Promise<void> {
  if (typeof window === 'undefined') return

  try {
    clearStoredProfile()
    for (const key of SENSITIVE_SNAPSHOT_KEYS) {
      localStorage.removeItem(key)
    }
  } catch {
    // Ignore localStorage failures.
  }

  if (typeof caches === 'undefined') return

  try {
    const cacheNames = await caches.keys()
    await Promise.all(
      cacheNames
        .filter((name) => SENSITIVE_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix)))
        .map((name) => caches.delete(name)),
    )
  } catch {
    // Ignore cache deletion failures.
  }
}

/**
 * Returns the stored auth_key from localStorage (client-side only).
 */
export function getStoredAuthKey(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(AUTH_KEY_STORAGE)
  } catch {
    return null
  }
}

/**
 * Returns the auth_key from the cookie (client-side only).
 * Use this for auth checks to stay in sync with middleware (cookie is source of truth).
 */
export function getAuthCookie(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const match = document.cookie.match(
      new RegExp('(^| )' + AUTH_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]+)'),
    )
    const value = match ? decodeURIComponent(match[2].trim()) : null
    return value && value.length > 0 ? value : null
  } catch {
    return null
  }
}

/**
 * Stores auth_key in localStorage and cookie (client-side only).
 * Cookie enables middleware to redirect unauthenticated users before page render.
 */
export function setStoredAuthKey(authKey: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(AUTH_KEY_STORAGE, authKey)
    const secure =
      typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(authKey)}; path=/; max-age=${AUTH_COOKIE_MAX_AGE}; SameSite=Lax${secure}`
  } catch {
    // Ignore quota exceeded or other storage errors
  }
}

export function setRefreshKey(key: string, expiry?: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(REFRESH_NAME, key)
    const secure =
      typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${REFRESH_NAME}=${encodeURIComponent(key)}; path=/; ${expiry ? `expires=${expiry}` : `max-age=${REFRESH_MAX_AGE}`}; SameSite=Lax${secure}`
  } catch {
    // Ignore quota exceeded or other storage errors
  }
}

/**
 * Removes auth_key from localStorage and cookie (client-side only).
 * Also clears dealer profile and other sensitive client caches.
 */
export function clearStoredAuthKey(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(AUTH_KEY_STORAGE)
    // Clear cookie with matching path; use both max-age and expires for broad browser support
    const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
    document.cookie = `${AUTH_COOKIE_NAME}=; path=/; ${expires}; max-age=0; SameSite=Lax`
    void clearSensitiveClientCaches()
  } catch {
    // Ignore
  }
}

/**
 * Server-side auth check (Server Components, API routes, Route Handlers).
 * Decodes the cookie value (stored with encodeURIComponent) before returning,
 * since the ERP expects the raw cookie string (e.g. full_name=...; sid=...).
 */
export function getLoggedInToken(cookies: ReadonlyRequestCookies): string | boolean {
  try {
    const authKey = cookies.get(AUTH_COOKIE_NAME)?.value

    if (!authKey) return false
    try {
      return decodeURIComponent(authKey) as string
    } catch {
      return authKey
    }
  } catch {
    return false
  }
}

export function getRefreshToken(cookies: ReadonlyRequestCookies): string | boolean {
  try {
    const authKey = cookies.get(REFRESH_NAME)?.value

    if (!authKey) return false
    try {
      return decodeURIComponent(authKey) as string
    } catch {
      return authKey
    }
  } catch {
    return false
  }
}

/**
 * Server-side auth check (Server Components, API routes, Route Handlers)
 */
export function isLoggedInServer(cookies: ReadonlyRequestCookies): boolean {
  try {
    const authKey = cookies.get(AUTH_COOKIE_NAME)?.value
    return Boolean(authKey && authKey.length > 0)
  } catch {
    return false
  }
}

/**
 * Client-side auth check (Client Components only)
 */
export function isLoggedInClient(): boolean {
  const authKey = getAuthCookie()
  return Boolean(authKey && authKey.length > 0)
}

export function updateGalliumCookie(cookies: ReadonlyRequestCookies, setCookieHeader: string) {
  const existingCookie = cookies.get(AUTH_COOKIE_NAME)?.value

  if (!existingCookie && !setCookieHeader) return ''
  if (!existingCookie) return setCookieHeader
  if (!setCookieHeader) return existingCookie

  const cookieObj: Record<string, string> = {}

  // parse existing cookie
  existingCookie.split(';').forEach((pair) => {
    const [key, ...rest] = pair.trim().split('=')
    if (!key) return
    cookieObj[key] = rest.join('=')
  })

  // parse new cookie values
  setCookieHeader.split(';').forEach((pair) => {
    const [key, ...rest] = pair.trim().split('=')
    if (!key) return
    cookieObj[key] = rest.join('=') // overwrite or add
  })
  // rebuild cookie string
  return Object.entries(cookieObj)
    .map(([k, v]) => `${k}=${v}`)
    .join('; ')
}

export function getUserIdFromAuthCookie(cookies: ReadonlyRequestCookies | RequestCookies) {
  const existingCookie = cookies.get(AUTH_COOKIE_NAME)?.value
  if (!existingCookie) return undefined

  for (const pair of existingCookie.split(';')) {
    const [key, value] = pair.trim().split('=')

    if (key === 'user_id' || key === 'userId' || key === 'user_Id') {
      return value ?? ''
    }
  }

  return undefined
}
